import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import {
    AddCircleBlue,
    CustomDelByNote,
    CustomDelete,
    DateRowStartEnd,
    EditWithIcon,
    FullTable,
    Loader, ManageActions,
    NoItemsYet,
    NonBillable,
    SimpleTooltip,
} from 'components';
import {
    checkIfDateIsExpired,
    FindLoad,
    FindSuccessItem,
    Images,
    PermissionList,
    RolePermission,
    useModal,
} from 'utils';
import { clientActions, httpRequestsOnSuccessActions } from 'store';
import { AUTH_ACTION_TYPE, authorizationBody, authorizationHead, authServiceNotYet } from './constants';
import { AuthorizationAppt } from './common';
import { AddSingleAuth, AddClientAuthorization, AuthFiles } from '../../clientModals';

const GET_ACTION_TYPE = 'GET_CLIENT_AUTHORIZATIONS';
const INACTIVATE_ACTION_TYPE = 'INACTIVATE_CLIENT_AUTHORIZATION';
const CANCEL_ACTION_TYPE = 'CANCEL_CLIENT_AUTHORIZATION';
const DELETE_ACTION_TYPE = 'DELETE_CLIENT_AUTHORIZATION';
const DELETE_SERVICE_ACTION_TYPE = 'DELETE_AUTHORIZED_SERVICE';

const CLIENT_AUTHORIZATION_VISIBLE = 'CLIENT_AUTHORIZATION_VISIBLE';

const hasUsageUnits = (service) => (service?.completedUnits > 0 || service?.reservedUnits > 0);
const hasAuthUsageUnits = (authorization) => authorization?.authorizedServices?.some(hasUsageUnits);
const isAuthInactive = (authorization) => authorization?.authorizedServices?.every((s) => !s?.active);

export const Authorization = ({}) => {
    const clientsAuthorizations = useSelector((state) => state.client.clientsAuthorizations);
    const client = useSelector((state) => state.client.clientItemInfo);
    const loader = FindLoad(GET_ACTION_TYPE);
    const dispatch = useDispatch();
    const [expanded, setExpanded] = useState(0);
    const { open } = useModal();
    const params = useParams();
    const [currentId, setCurrentId] = useState(null);
    const successVisible = FindSuccessItem(CLIENT_AUTHORIZATION_VISIBLE);

    useEffect(() => {
        if (successVisible) {
            setCurrentId(null);
            dispatch(httpRequestsOnSuccessActions.removeSuccess(CLIENT_AUTHORIZATION_VISIBLE));
        }
    }, [successVisible]);

    useEffect(() => {
        dispatch(clientActions.getClientAuthorization(params?.id));
        dispatch(clientActions.getEnrollmentPayers(params?.id));
        // dispatch(fundingSourceActions.getFundingSource({ status: 'ACTIVE' }));
    }, []);

    const handleExpand = (id) => {
        setExpanded(expanded === id ? '' : id);
    };

    const openModal = (item, status) => {
        open(
            <AuthorizationAppt info={{...item, itemStatus: status,}} />
        )
    };

    const handleEdit = (item, name) => {
        if (name === 'edit') {
            open(
                <AddSingleAuth
                    item={{
                        ...item,
                        authorizationId: item?.authorizationId,
                        payerId: item?.payerId,
                        serviceId: item?.service?._id,
                        totalUnits: item?.totalUnits,
                        frequencyLimit: {
                            mode: item?.frequencyLimit?.mode,
                            limit: item?.frequencyLimit?.limit,
                        },
                        chargeRateIds: item?.chargeRates?.map((i) => i?.id),
                        chargeRateList: item?.chargeRates,
                        startDate: item?.startDate,
                        endDate: item?.endDate,
                        edit: true,
                    }}
                />,
            );
        }
        if (name === 'inactivate') {
            open(
                <CustomDelByNote
                    info="Deactivate Authorized Service"
                    text="Are you sure you want to deactivate this authorized service? Once deactivated, this service cannot be used to create new appointments."
                    handleDel={(comment) => {
                        dispatch(clientActions.inactivateClientAuthorization(item?.id, client?.id, comment));
                    }}
                    innerText={'Deactivate'}
                    inputLabel={'Reason for deactivation'}
                    actionType={INACTIVATE_ACTION_TYPE}
                />,
            );
        }
        if (name === 'apptVisible') {
            setCurrentId(item?.id);
            dispatch(clientActions.clientAuthorizationVisible(item?.id, client?.id, !item?.apptVisible));
        }
        if (name === 'delete') {
            if (hasUsageUnits(item)) return;
            open(
                <CustomDelete
                    info="Delete Authorized Service?"
                    text="Are you sure you want to delete this authorized service? This action cannot be undone."
                    handleDel={() => {
                        dispatch(clientActions.deleteAuthorizedService(item?.id, client?.id));
                    }}
                    innerText="Delete"
                    actionType={DELETE_SERVICE_ACTION_TYPE}
                    body={
                        <div className="deleting-client-info-box" style={{ alignItems: 'flex-start' }}>
                            <img style={{ marginTop: 2 }} src={Images.shield} alt="icon" />
                            <p style={{ fontWeight: 600 }}>#{item?.authorizationId}</p>
                        </div>
                    }
                />,
            );
        }
    };

    if (loader?.length) {
        return <Loader />;
    }

    return (
        <div>
            {RolePermission([PermissionList.CLIENT_AUTHORIZATION_MANAGE?.code]) && (
                <div className="header-action-btn">
                    <AddCircleBlue text={'Add Authorization'} handleClick={() => open(<AddClientAuthorization />)} />
                </div>
            )}
            {clientsAuthorizations?.length ? (
                <div>
                    <div className="auth-table-wrapper">
                        {clientsAuthorizations?.map((service, index) => {
                            return (
                                <Accordion key={index} expanded={expanded === index}
                                           onChange={() => handleExpand(index)}>
                                    <AccordionSummary>
                                        <div className="flex-align-center" style={{ gap: 8 }}>
                                            <div className="flex-align-center" style={{ gap: 8 }}>
                                                <p className="auth-table-title">#{service?.authorizationId}</p>
                                                {service?.billable === false && (
                                                    <NonBillable toolText={'Non-billable authorization'} noText={true} />
                                                )}
                                                <div
                                                    style={{ background: checkIfDateIsExpired(service)?.background }}
                                                    className="auth-table-date-box"
                                                >
                                                    <div style={{ background: checkIfDateIsExpired(service)?.color }} />
                                                    <p>
                                                        <DateRowStartEnd start={service?.startDate} end={service?.endDate} />
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex-align-center" style={{ gap: 8 }}>
                                                <p className="auth-table-title">Payer:</p>
                                                <div className="business-center-wrapper">
                                                    <img src={Images.businessCenter} alt="businessCenter" />
                                                    <p>{service?.payerName}</p>
                                                </div>
                                            </div>
                                            {service?.renderingProviderName && (
                                                <div className="flex-align-center" style={{ gap: 8 }}>
                                                    <p className="auth-table-title">Provider:</p>
                                                    <div className="provider-wrapper">
                                                        <img src={Images.providerIcon} alt="providerIcon" />
                                                        <p style={{ color: '#026AA2' }}>{service?.renderingProviderName}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex-align-center" style={{ gap: 8 }}>
                                            {/*<SimpleTooltip title={<p>{'Edit Authorization'}</p>} placement="top-end">*/}
                                            {/*    <div>*/}
                                            {/*        <EditWithIcon*/}
                                            {/*            icon={Images.editAuth}*/}
                                            {/*            type={'button'}*/}
                                            {/*            handleClick={(e) => {*/}
                                            {/*                open(<AddClientAuthorization item={service} />);*/}
                                            {/*                e.stopPropagation();*/}
                                            {/*            }}*/}
                                            {/*        />*/}
                                            {/*    </div>*/}
                                            {/*</SimpleTooltip>*/}
                                            <SimpleTooltip title={<p>{'Authorization files'}</p>} placement="top-end">
                                                <div>
                                                    <EditWithIcon
                                                        icon={Images.fileAuth}
                                                        type={'button'}
                                                        handleClick={(e) => {
                                                            open(<AuthFiles item={service} />);
                                                            e.stopPropagation();
                                                        }}
                                                    />
                                                </div>
                                            </SimpleTooltip>
                                            <SimpleTooltip title={<p>{'Clone this Authorization'}</p>}
                                                           placement="top-end">
                                                <div>
                                                    <EditWithIcon
                                                        icon={Images.copyAuth}
                                                        type={'button'}
                                                        handleClick={(e) => {
                                                            open(
                                                                <AddClientAuthorization
                                                                    item={{ ...service, authorizationId: null }}
                                                                    clone={true}
                                                                />,
                                                            );
                                                            e.stopPropagation();
                                                        }}
                                                    />
                                                </div>
                                            </SimpleTooltip>

                                            <div>
                                                <ManageActions
                                                    styleBodyItems={{ padding: 0, width: 200 }}
                                                    btnIcon={Images.moreGrayIcon}
                                                    btnClass={'edit-btn-box'}
                                                    renderBody={(handleClose) => (
                                                        <div>
                                                            <button
                                                                className="authorization-action-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleClose();
                                                                    open(<AddClientAuthorization item={service} />);
                                                                }}
                                                            >
                                                                <img src={Images.edit} alt="edit" />
                                                                <p>Edit</p>
                                                            </button>

                                                            <SimpleTooltip
                                                                title={
                                                                    isAuthInactive(service)
                                                                        ? <p>Authorization is already cancelled</p>
                                                                        : hasAuthUsageUnits(service)
                                                                            ?
                                                                            <p>Cannot cancel: authorization has reserved or
                                                                                completed units</p>
                                                                            : ''
                                                                }
                                                                placement="top"
                                                            >
                                                                <button
                                                                    className="authorization-action-btn"
                                                                    style={(hasAuthUsageUnits(service) || isAuthInactive(service)) ? {
                                                                        opacity: 0.4,
                                                                        cursor: 'not-allowed',
                                                                    } : {}}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (hasAuthUsageUnits(service) || isAuthInactive(service)) return;
                                                                        handleClose();
                                                                        open(
                                                                            <CustomDelByNote
                                                                                info="Cancel Authorization"
                                                                                text="Are you sure you want to cancel this auth.? Cancelled authorizations will not be available for scheduling."
                                                                                handleDel={(comment) => {
                                                                                    dispatch(
                                                                                        clientActions.cancelClientAuthorization(
                                                                                            client?.id,
                                                                                            service?.authorizationUniqueId,
                                                                                            service?.payerId,
                                                                                            comment,
                                                                                        ),
                                                                                    );
                                                                                }}
                                                                                innerText="Cancel Authorization"
                                                                                inputLabel="Reason for cancellation"
                                                                                actionType={CANCEL_ACTION_TYPE}
                                                                            />,
                                                                        );
                                                                    }}>
                                                                    <img src={Images.cancelCircle} alt="delete" />
                                                                    <p> Cancel Authorization</p>
                                                                </button>
                                                            </SimpleTooltip>

                                                            <SimpleTooltip
                                                                title={hasAuthUsageUnits(service) ?
                                                                    <p>Cannot delete: authorization has reserved or
                                                                        completed units</p> : ''}
                                                                placement="top"
                                                            >
                                                                <button
                                                                    className="authorization-action-btn"
                                                                    style={hasAuthUsageUnits(service) ? {
                                                                        opacity: 0.4,
                                                                        cursor: 'not-allowed',
                                                                    } : {}}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (hasAuthUsageUnits(service)) return;
                                                                        handleClose();
                                                                        open(
                                                                            <CustomDelete
                                                                                info="Delete Authorization?"
                                                                                text="Are you sure you want to delete this authorization? This action cannot be undone."
                                                                                handleDel={() => {
                                                                                    dispatch(
                                                                                        clientActions.deleteClientAuthorization(
                                                                                            client?.id,
                                                                                            service?.authorizationUniqueId,
                                                                                            service?.payerId,
                                                                                        ),
                                                                                    );
                                                                                }}
                                                                                innerText="Delete Authorization"
                                                                                actionType={DELETE_ACTION_TYPE}
                                                                                body={
                                                                                    <div
                                                                                        className="deleting-client-info-box"
                                                                                        style={{ alignItems: 'flex-start' }}>
                                                                                        <img style={{ marginTop: 2 }}
                                                                                             src={Images.shield}
                                                                                             alt="icon" />
                                                                                        <p style={{ fontWeight: 600 }}>#{service?.authorizationId}</p>
                                                                                    </div>
                                                                                }
                                                                            />,
                                                                        );
                                                                    }}>
                                                                    <img src={Images.redTrash} alt="delete" />
                                                                    <p> Delete Authorization</p>
                                                                </button>
                                                            </SimpleTooltip>
                                                        </div>
                                                    )}
                                                />
                                            </div>

                                            <div className="accordion-drops">
                                                <img src={expanded === index ? Images.dropUp : Images.dropDown}
                                                     alt="icon" />
                                            </div>
                                        </div>
                                    </AccordionSummary>

                                    <AccordionDetails>
                                        <div className="authorized-services-header">
                                            <p>Authorized Services</p>
                                            {/*{!isAuthInactive(service) && (*/}
                                            {service?.active ?
                                                <AddCircleBlue
                                                    type="button"
                                                    text={'Add Auth Service'}
                                                    handleClick={() => open(<AddSingleAuth item={service} />)}
                                                />
                                                :
                                                <div>
                                                    <SimpleTooltip placement="top-start"
                                                                   title="This authorization is cancelled, you cannot add services to a cancelled authorization">
                                                        <div>
                                                            <AddCircleBlue
                                                                styles={{ opacity: 0.5 }}
                                                                type="button"
                                                                text={'Add Auth Service'}
                                                            />
                                                        </div>
                                                    </SimpleTooltip>
                                                </div>

                                            }
                                            {/*)}*/}
                                        </div>
                                        <FullTable
                                            head={authorizationHead}
                                            body={authorizationBody(openModal, currentId)}
                                            loadingType={AUTH_ACTION_TYPE}
                                            list={service?.authorizedServices}
                                            height={'auto'}
                                            handleClickButton={handleEdit}
                                            notYet={authServiceNotYet}
                                            activeRowId={''}
                                        />
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <NoItemsYet
                    text={'No Authorizations Yet'}
                    subTitle={'Add authorizations to track client approvals and permissions.'}
                    image={Images.noFsYet}
                />
            )}
        </div>
    );
};
